import classNames from 'classnames';
import React, { useRef, FC, useEffect, useState } from 'react';


import styles from '../desktop/saved-payment-method.less';
import { PaymentMethodInfo, PaymentMethodType } from 'src/invoice/types/types';
import { PaymentMethodModal } from '../../payment-method-modal/payment-method-modal';
import { Chip } from '@material-ui/core';
import { PaymentAmountFormState } from '../../payment-amount/payment-amount';
import { PendingInvoicePayment, SavedPaymentMethodsStore } from 'src/payment-method/stores/saved-payment-methods.store';
import { ChargeType, SplitInfo } from 'src/api';
import { MatField } from 'src/core/components/mat-field';
import { FieldState } from 'formstate';

interface SavedPaymentMethodProps {
    paymentMethod: PaymentMethodInfo;
    setPaymentMethod: (paymentMethod: PaymentMethodInfo) => void;
    setPaymentMethodAction: (action: () => void) => void;
    setSecurityCodeValid: (isValid: boolean) => void;
    paymentAmountData: PaymentAmountFormState;
    usePaymentGateway: () => boolean;
    invoiceId?: string;
    splits?: SplitInfo[];
    store: SavedPaymentMethodsStore;
    chargeType: ChargeType;
    statementId?: string;
}

export const SavedPaymentMethod: FC<SavedPaymentMethodProps> = ({
    paymentMethod,
    setPaymentMethod,
    usePaymentGateway,
    paymentAmountData,
    setSecurityCodeValid,
    setPaymentMethodAction,
    invoiceId,
    store,
    chargeType,
    splits,
    statementId
}) => {

    const [isModalOpen, setIsModalOpen] = useState(false);

    const securityCodeValidator = (value: String) => {
        let message = '';
        const code = Number(value);

        if (!value) {
            message = 'Security code is required.';
        }
        else if (isNaN(code)) {
            message = 'Security code must be a number.';
        }
        else if (value.length < 3) {
            message = 'Security code must be at least 3 digits'
        }

        if (message) {
            return message;
        }
    };

    const securityCode = useRef(new FieldState('').validators(securityCodeValidator));
    const [showCvc, setShowCvc] = useState(false);
    const useCvc = useRef(false);
    const currentPaymentMethod = useRef(paymentMethod);
    const [currentPaymentMethodId, setCurrentPaymentMethodId] = useState(paymentMethod.paymentMethodId);

    const onChangePaymentMethod = () => {
        setIsModalOpen(true);
    };

    const onModalClose = () => {
        setIsModalOpen(false);
    };

    const updateCurrentPaymentMethod = (paymentMethod: PaymentMethodInfo) => {
        setPaymentMethod(paymentMethod);

        if (currentPaymentMethod.current.paymentMethodId !== paymentMethod.paymentMethodId) {
            securityCode.current.value = '';
            setSecurityCodeValid(!showCvc);
        }

        currentPaymentMethod.current = paymentMethod;
        setCurrentPaymentMethodId(paymentMethod.paymentMethodId);
    };

    const paymentMethodAction = async () => {
        const paymentMethod = currentPaymentMethod.current;
        const amount = (!paymentAmountData.isFullBalance.value
            ? parseFloat(paymentAmountData.customAmount.value)
            : paymentAmountData.fullBalance) || 0;

        const payment: PendingInvoicePayment = {
            amount,
            invoiceNumber: invoiceId!,
            amountRemaining: paymentAmountData.fullBalance! - amount,
            chargeType,
            splits,
            statementId
        };

        if (useCvc.current && paymentMethod.paymentMethodType === PaymentMethodType.CreditCard) {
            paymentMethod.cvv = securityCode.current.value;
        }

        await store.chargePaymentMethod(paymentMethod, payment);
    };

    const securityCodeChanged = async () => {
        const validation = await securityCode.current.validate();
        setSecurityCodeValid(!showCvc || !validation.hasError)
    };

    const showOrHideCvc = async () => {
        const invoice = invoiceId ? await store.getInvoice(invoiceId || '')
            : await store.getInvoiceByStatement(statementId || '');
        const shouldShowCvc = invoice?.usesPaymentsService || false;
        setShowCvc(shouldShowCvc);
        setSecurityCodeValid(!shouldShowCvc);
        useCvc.current = shouldShowCvc;
    };

    useEffect(() => {
        setPaymentMethodAction(paymentMethodAction);
        showOrHideCvc();
    }, []);

    return (
        <>
            <div className={classNames('saved-account-container', styles.savedAccountContainer)}>
                <div className={classNames('saved-account-container-top', styles.savedAccountContainerTop)}>
                    <img className={classNames('saved-account-logo', styles.savedAccountLogo)} src={paymentMethod.icon || 'assets/images/browser-firefox.png'} alt={paymentMethod.displayText} />
                    <div className={classNames('saved-account-text', styles.savedAccountText)}>
                        {paymentMethod.displayText}
                    </div>
                    {(paymentMethod.primary || paymentMethod.disconnected) && (
                        <div className={classNames('saved-account-chip-holder', styles.savedAccountChipHolder)}>
                            {paymentMethod.primary && (<Chip label="Primary" size="small" className={styles.primaryChipColor} />)}
                            {paymentMethod.disconnected && (<Chip label="Connection Lost" size="small" className={styles.disconnectedChipColor} />)}
                        </div>
                    )}
                </div>
                {showCvc && paymentMethod.paymentMethodType === PaymentMethodType.CreditCard && (<div className={classNames('saved-account-container-bottom', styles.savedAccountContainerBottom)}>
                    <span>Enter the security code on this card to finish payment.</span>
                    <MatField
                        fieldState={securityCode.current}
                        autoComplete="off"
                        fullWidth={undefined}
                        maxLength={4}
                        className={classNames('security-code-field', styles.securityCodeField)}
                        placeholder='Security Code'
                        onFocus={securityCodeChanged}
                        onBlur={securityCodeChanged}
                        onKeyUp={securityCodeChanged}
                    />
                </div>)}
            </div>
            <span className={styles.savedAccountChangePayment}>
                <a onClick={onChangePaymentMethod}>Change payment method</a>
            </span>

            {isModalOpen && (
                <PaymentMethodModal
                    selectedPaymentMethodId={currentPaymentMethodId}
                    onPaymentMethodSelected={updateCurrentPaymentMethod}
                    chargeType={chargeType}
                    onClose={onModalClose}
                    isOpen={isModalOpen}
                    invoiceId={invoiceId}
                    usePaymentGateway={usePaymentGateway}
                    usePaymentsService={store.usesPaymentsService}
                    store={store}
                    splits={splits}
                    statementId={statementId}
                />
            )}
        </>
    )
};